// Development utilities for environment-sensitive operations with security focus

// List of sensitive field names that should be redacted
const SENSITIVE_FIELDS = [
  'password', 'passwd', 'pwd', 'secret', 'token', 'key', 'apikey', 'api_key',
  'auth', 'authorization', 'bearer', 'session', 'cookie', 'csrf', 'xsrf',
  'credit_card', 'creditcard', 'cc', 'cvv', 'ssn', 'social_security',
  'email', 'phone', 'address', 'ip', 'ipaddress', 'user_id', 'userid',
  'username', 'login', 'hash', 'salt', 'nonce', 'signature', 'private',
  'confidential', 'sensitive', 'personal', 'pii', 'gdpr'
];

// Check if we're in development environment or dev logging is enabled
const isDevelopment = () => {
  // Check for development environment indicators
  const nodeEnv = process.env.NODE_ENV === 'development';
  const hostname = typeof window !== 'undefined' && 
    (window.location.hostname.includes('development') ||
     window.location.hostname.includes('localhost') ||
     window.location.port === '3000');
  
  // Check the public environment variable for dev logging
  const devLoggingEnabled = process.env.GADGET_PUBLIC_ENABLE_DEV_LOGGING === 'true';
  
  return nodeEnv || hostname || devLoggingEnabled;
};

// Check if a field name is sensitive
const isSensitiveField = (fieldName) => {
  if (typeof fieldName !== 'string') return false;
  const lowerField = fieldName.toLowerCase();
  return SENSITIVE_FIELDS.some(sensitive => lowerField.includes(sensitive));
};

// Sanitize data by redacting sensitive fields
const sanitizeData = (data, maxDepth = 3, currentDepth = 0) => {
  // Prevent infinite recursion
  if (currentDepth >= maxDepth) {
    return '[MAX_DEPTH_REACHED]';
  }

  // Handle null/undefined
  if (data == null) {
    return data;
  }

  // Handle primitives
  if (typeof data !== 'object') {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item, maxDepth, currentDepth + 1));
  }

  // Handle objects
  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    if (isSensitiveField(key)) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value, maxDepth, currentDepth + 1);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

// Safe console logging that only works in development
const devLog = (...args) => {
  if (isDevelopment()) {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? sanitizeData(arg) : arg
    );
    console.log(...sanitizedArgs);
  }
};

// Safe warning logging
const devWarn = (...args) => {
  if (isDevelopment()) {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? sanitizeData(arg) : arg
    );
    console.warn(...sanitizedArgs);
  }
};

// Safe error logging (works in all environments but sanitized in production)
const devError = (...args) => {
  if (isDevelopment()) {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? sanitizeData(arg) : arg
    );
    console.error(...sanitizedArgs);
  } else {
    // In production, only log generic error without sensitive data
    console.error('An error occurred during processing');
  }
};

// Safe data logging that strips sensitive information in production
const devLogData = (label, data) => {
  if (isDevelopment()) {
    const sanitizedData = typeof data === 'object' && data !== null ? sanitizeData(data) : data;
    console.log(label, sanitizedData);
  }
  // Completely silent in production - no logging at all
};

// Safe JSON logging for complex data structures
const devLogJSON = (label, data) => {
  if (isDevelopment()) {
    const sanitizedData = typeof data === 'object' && data !== null ? sanitizeData(data) : data;
    console.log(label, JSON.stringify(sanitizedData, null, 2));
  }
  // Completely silent in production
};

// Log object properties with automatic sanitization
const devLogObject = (label, obj) => {
  if (isDevelopment() && obj && typeof obj === 'object') {
    const sanitized = sanitizeData(obj);
    console.log(label);
    console.table(sanitized);
  }
};

// Safe timing utilities for performance debugging
const devTime = (label) => {
  if (isDevelopment()) {
    console.time(label);
  }
};

const devTimeEnd = (label) => {
  if (isDevelopment()) {
    console.timeEnd(label);
  }
};

// Safe group logging
const devGroup = (label) => {
  if (isDevelopment()) {
    console.group(label);
  }
};

const devGroupEnd = () => {
  if (isDevelopment()) {
    console.groupEnd();
  }
};

// Development-only function execution
const devOnly = (fn) => {
  if (isDevelopment()) {
    try {
      fn();
    } catch (error) {
      devError('Error in devOnly function:', error);
    }
  }
};

// Safe assertion for development
const devAssert = (condition, message) => {
  if (isDevelopment() && !condition) {
    console.error('Assertion failed:', message);
    throw new Error(`Assertion failed: ${message}`);
  }
};

// Create a development-safe logger with consistent API
const createDevLogger = (prefix = '') => {
  const logPrefix = prefix ? `[${prefix}]` : '';
  
  return {
    log: (...args) => devLog(logPrefix, ...args),
    warn: (...args) => devWarn(logPrefix, ...args),
    error: (...args) => devError(logPrefix, ...args),
    data: (label, data) => devLogData(`${logPrefix} ${label}`, data),
    json: (label, data) => devLogJSON(`${logPrefix} ${label}`, data),
    object: (label, obj) => devLogObject(`${logPrefix} ${label}`, obj),
    time: (label) => devTime(`${logPrefix} ${label}`),
    timeEnd: (label) => devTimeEnd(`${logPrefix} ${label}`),
    group: (label) => devGroup(`${logPrefix} ${label}`),
    groupEnd: devGroupEnd,
    assert: (condition, message) => devAssert(condition, `${logPrefix} ${message}`)
  };
};

export {
  isDevelopment,
  isSensitiveField,
  sanitizeData,
  devLog,
  devWarn,
  devError,
  devLogData,
  devLogJSON,
  devLogObject,
  devTime,
  devTimeEnd,
  devGroup,
  devGroupEnd,
  devOnly,
  devAssert,
  createDevLogger
};